import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { TranslatePipe } from '../../pipes/translate.pipe';
import { ApprovalFlowDisplayComponent } from '../shared/approval-flow-display/approval-flow-display.component';
import { FormFieldConfig } from '../dynamic-form/dynamic-form/dynamic-form.component';
import { environment } from '../../services/global-environment';
import { AuthService } from '../../services/auth.service';
import { FileUploadService } from '../../services/file-upload.service';
import { I18nService } from '../../services/i18n.service';

@Component({
  selector: 'app-form-display',
  standalone: true,
  imports: [CommonModule, TranslatePipe, ApprovalFlowDisplayComponent],
  templateUrl: './form-display.component.html',
  styleUrls: ['./form-display.component.scss']
})
export class FormDisplayComponent implements OnInit {
  @Input() config: FormFieldConfig[] = [];
  @Input() formData: any = {};
  @Input() formName: string = '';

  @Input() formid: string = '';
  @Input() recordId: string = '';

  @Output() approvalComplete = new EventEmitter<any>();

  // 簽核完成標記 - 記錄是否有簽核完成（供父元件查詢）
  private hasApprovalCompleted: boolean = false;
  private latestApprovalData: any = null;


  // API URL
  private apiUrl = environment.apiUrl;
  private companyId = environment.companyid;

  // 子表單記錄計數緩存
  private subformRecordCountCache: { [key: string]: { count: number; timestamp: number } } = {};
  private readonly CACHE_DURATION = 30000; // 30秒緩存

  // 🔑 子表單計數快速查詢 Map（避免模板中重複呼叫函數）
  public subformCountMap: Map<string, number> = new Map();

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private fileUploadService: FileUploadService,
    private i18nService: I18nService
  ) {}

  ngOnInit(): void {
    // console.log("=== Form Display Component 初始化 ===");
    // console.log("formid:", this.formid);
    // console.log("recordId:", this.recordId);
    // console.log("formName:", this.formName);
    // console.log("config:", this.config);
    // console.log("formData:", this.formData);
    // console.log("==========================================");

    // 如果 formid、recordId 有值，且 config 跟 formData 為空，分別載入資料
    if (this.formid && this.recordId && (!this.config || this.config.length === 0) && (!this.formData || Object.keys(this.formData).length === 0)) {
      this.loadFormConfigAndData();
    }
  }

  /**
   * 載入表單配置和資料
   */
  private async loadFormConfigAndData(): Promise<void> {
    try {
      // 並行載入表單配置和資料
      const [configResult, dataResult] = await Promise.all([
        this.loadFormConfig(),
        this.loadFormData()
      ]);

      // console.log('表單配置和資料載入完成', { configResult, dataResult });

      // 載入完成後，批次取得所有子表單記錄計數
      await this.loadAllSubformCountsBatch();
    } catch (error) {
      console.error('載入表單配置或資料失敗:', error);
    }
  }

  /**
   * 透過 formid 取得表單配置
   */
  private async loadFormConfig(): Promise<any> {
    try {
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      const params = {
        formid: this.formid
      };

      // console.log('正在載入表單配置:', params);

      const response = await this.http.get<any>(
        `${this.apiUrl}/api/form-manager/get_specific_form_config`,
        { headers, params }
      ).toPromise();

      if (response && response.success && response.data) {
        // console.log('成功取得表單配置:', response.data);

        // 設定 config
        if (response.data.config && Array.isArray(response.data.config)) {
          this.config = response.data.config;
        }

        return response.data;
      } else {
        console.warn('表單配置回應格式不正確或無資料:', response);
        return null;
      }
    } catch (error) {
      console.error('載入表單配置失敗:', error);
      throw error;
    }
  }

  /**
   * 透過 recordId 取得表單資料
   */
  private async loadFormData(): Promise<any> {
    try {
      const headers = new HttpHeaders({ 'Content-Type': 'application/json' });
      const params = {
        id: this.recordId
      };

      // console.log('正在載入表單資料:', params);

      const response = await this.http.get<any>(
        `${this.apiUrl}/api/form-manager/get-form-data`,
        { headers, params }
      ).toPromise();

      if (response && response.success && response.data) {
        // console.log('成功取得表單資料:', response.data);

        // 設定 formData
        this.formData = response.data;

        return response.data;
      } else {
        console.warn('表單資料回應格式不正確或無資料:', response);
        return null;
      }
    } catch (error) {
      console.error('載入表單資料失敗:', error);
      throw error;
    }
  }

  /**
   * 格式化時間戳記顯示 - 直接處理毫秒級時間戳記
   */
  formatTimestamp(timestamp: number): string {
    if (!timestamp || isNaN(timestamp)) {
      return '';
    }

    try {
      const date = new Date(timestamp);
      
      if (isNaN(date.getTime())) {
        return timestamp ? timestamp.toString() : '';
      }

      // 格式化為 yyyy-mm-dd HH:mm:ss
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      const seconds = String(date.getSeconds()).padStart(2, '0');
      
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
      console.error('時間戳格式化錯誤:', error);
      return timestamp ? timestamp.toString() : '';
    }
  }

  /**
   * 取得子表單資料筆數（同步方法，優先使用緩存）
   * @param formId 子表單ID
   * @param parentFormId 父記錄ID（使用當前記錄的ID）
   * @returns 資料筆數
   */
  getSubformRecordCount(formId: any): number {
    // 處理複雜的 formId 物件結構
    let actualFormId: string = '';

    if (typeof formId === 'string') {
      actualFormId = formId.trim();
    } else if (formId && typeof formId === 'object') {
      // 處理 subform-relation 物件結構
      if (formId.originalValue) {
        actualFormId = formId.originalValue;
      } else if (formId.value && formId.value.originalValue) {
        actualFormId = formId.value.originalValue;
      } else if (formId.value && typeof formId.value === 'string') {
        actualFormId = formId.value;
      }
    }

    if (!actualFormId || !this.recordId) {
      return 0;
    }

    // 檢查緩存，緩存鍵包含 parent_formid
    const cacheKey = `${this.companyId}_${actualFormId}_${this.recordId}`;
    const cached = this.subformRecordCountCache[cacheKey];
    const now = Date.now();

    if (cached && (now - cached.timestamp) < this.CACHE_DURATION) {
      return cached.count;
    }

    // 緩存過期或不存在，非同步載入新資料
    this.loadSubformRecordCount(actualFormId, this.recordId);

    // 返回緩存值或預設值
    return cached ? cached.count : 0;
  }

  /**
   * 非同步載入子表單資料筆數（單一查詢，僅在緩存未命中時使用）
   * @param formId 子表單ID
   * @param parentFormId 父記錄ID
   */
  private loadSubformRecordCount(formId: string, parentFormId: string): void {
    const cacheKey = `${this.companyId}_${formId}_${parentFormId}`;

    // 使用與 showlist 相同的 API 端點
    const url = `${this.apiUrl}/api/form-manager/get_form_container_count`;
    const params = new URLSearchParams({
      formid: formId,
      parent_formid: parentFormId
    });
    const fullUrl = `${url}?${params.toString()}`;

    this.http.get<any>(fullUrl).subscribe({
      next: (response) => {
        const count = response.success ? (response.data || 0) : 0;

        // 更新緩存
        this.subformRecordCountCache[cacheKey] = {
          count: count,
          timestamp: Date.now()
        };
      },
      error: (error) => {
        console.error('載入子表單記錄計數失敗:', error);

        // 記錄錯誤到緩存中，避免重複請求
        this.subformRecordCountCache[cacheKey] = {
          count: 0,
          timestamp: Date.now()
        };
      }
    });
  }

  /**
   * 批次載入所有子表單資料筆數（效能優化）
   * 在表單載入時一次性取得所有子表單的記錄計數
   */
  private async loadAllSubformCountsBatch(): Promise<void> {
    console.log('[批次載入] 開始執行 loadAllSubformCountsBatch');
    console.log('[批次載入] config:', this.config);
    console.log('[批次載入] recordId:', this.recordId);

    if (!this.config || !this.recordId) {
      console.log('[批次載入] 條件不符，提前返回 (config 或 recordId 為空)');
      return;
    }

    // 從 config 中收集所有 subform-relation 類型的欄位 ID
    const subformFieldIds: string[] = [];

    for (const field of this.config) {
      if (field.type === 'subform-relation' && field.name) {
        // 從 formData 中取得實際的 formId
        const fieldValue = this.formData[field.name];
        let actualFormId: string = '';

        if (typeof fieldValue === 'string') {
          actualFormId = fieldValue.trim();
        } else if (fieldValue && typeof fieldValue === 'object') {
          if (fieldValue.originalValue) {
            actualFormId = fieldValue.originalValue;
          } else if (fieldValue.value && fieldValue.value.originalValue) {
            actualFormId = fieldValue.value.originalValue;
          } else if (fieldValue.value && typeof fieldValue.value === 'string') {
            actualFormId = fieldValue.value;
          }
        }

        if (actualFormId) {
          console.log(`[批次載入] 找到子表單欄位: ${field.name} -> formId: ${actualFormId}`);
          subformFieldIds.push(actualFormId);
        }
      }
    }

    console.log('[批次載入] 收集到的子表單 formIds:', subformFieldIds);
    console.log('[批次載入] 子表單數量:', subformFieldIds.length);

    // 如果沒有子表單欄位，直接返回
    if (subformFieldIds.length === 0) {
      console.log('[批次載入] 沒有子表單欄位，提前返回');
      return;
    }

    // 🔑 關鍵優化：預先填充緩存佔位符，防止個別請求
    const now = Date.now();
    subformFieldIds.forEach(formId => {
      const cacheKey = `${this.companyId}_${formId}_${this.recordId}`;
      this.subformRecordCountCache[cacheKey] = {
        count: 0,
        timestamp: now
      };
      // 同時填入快速查詢 Map
      this.subformCountMap.set(formId, 0);
    });

    console.log(`[批次載入] ✅ 已預先填充 ${subformFieldIds.length} 個緩存佔位符（緩存 + Map），防止個別請求`);

    // 呼叫批次 API
    try {
      const url = `${this.apiUrl}/api/form-manager/get_form_container_counts_batch`;
      const body = {
        parent_formid: this.recordId,
        formids: subformFieldIds,
        includeDeleted: true
      };

      console.log('[批次載入] 準備發送批次 API 請求');
      console.log('[批次載入] URL:', url);
      console.log('[批次載入] Body:', body);

      const response = await this.http.post<any>(url, body).toPromise();

      console.log('[批次載入] API 回應:', response);

      if (response && response.success && response.data) {
        const responseTime = Date.now();

        console.log('[批次載入] 開始寫入緩存，資料:', response.data);

        // 將所有結果寫入緩存（覆蓋佔位符）
        for (const formId of subformFieldIds) {
          const cacheKey = `${this.companyId}_${formId}_${this.recordId}`;
          const count = response.data[formId] || 0;

          this.subformRecordCountCache[cacheKey] = {
            count: count,
            timestamp: responseTime
          };

          // 🔑 同時填入快速查詢 Map（供模板使用）
          this.subformCountMap.set(formId, count);

          console.log(`[批次載入] 緩存已更新: ${cacheKey} = ${count}`);
        }

        console.log('[批次載入] 所有緩存已更新完成');
        console.log('[批次載入] 當前緩存狀態:', this.subformRecordCountCache);
        console.log('[批次載入] 快速查詢 Map:', this.subformCountMap);
      } else {
        console.warn('[批次載入] API 回應格式不正確或無資料');
      }
    } catch (error) {
      console.error('[批次載入] 批次載入子表單記錄計數失敗:', error);
      // 錯誤時不做處理，讓個別的 loadSubformRecordCount 在需要時自行重試
    }
  }

  /**
   * 打開子表單列表頁面（新分頁）
   * @param formId 子表單ID
   */
  openSubformInNewTab(formId: any): void {
    // 處理複雜的 formId 物件結構
    let actualFormId: string = '';
    
    if (typeof formId === 'string') {
      actualFormId = formId.trim();
    } else if (formId && typeof formId === 'object') {
      // 處理 subform-relation 物件結構
      if (formId.originalValue) {
        actualFormId = formId.originalValue;
      } else if (formId.value && formId.value.originalValue) {
        actualFormId = formId.value.originalValue;
      } else if (formId.value && typeof formId.value === 'string') {
        actualFormId = formId.value;
      }
    }
    
    if (!actualFormId || !this.recordId) {
      return;
    }

    // 構建子表單列表頁面的 URL，使用現有的 form-list 路由
    const url = `/form-list?formid=${actualFormId}&parent_formid=${this.recordId}&companyid=${this.companyId}`;
    
    // 另開新分頁
    window.open(url, '_blank');
  }

  /**
   * 格式化日期時間顯示
   */
  formatDateTime(dateInput: any): string {
    if (!dateInput) return '';

    let date: Date;

    if (typeof dateInput === 'number') {
      // 判斷是否為秒級時間戳
      if (dateInput < 10000000000) {
        date = new Date(dateInput * 1000);
      } else {
        date = new Date(dateInput);
      }
    } else if (typeof dateInput === 'string') {
      // Check if the string is in date format (YYYY-MM-DD, YYYY/MM/DD, etc.)
      const dateFormatRegex = /^\d{4}[-/]\d{1,2}[-/]\d{1,2}/;
      if (dateFormatRegex.test(dateInput)) {
        date = new Date(dateInput);
      } else {
        // Only process as timestamp if it's a pure numeric string
        const numericValue = parseInt(dateInput);
        if (!isNaN(numericValue) && /^\d+$/.test(dateInput.trim())) {
          if (numericValue < 10000000000) {
            date = new Date(numericValue * 1000);
          } else {
            date = new Date(numericValue);
          }
        } else {
          date = new Date(dateInput);
        }
      }
    } else if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      return '';
    }

    // 檢查日期是否有效
    if (isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false  // Set to 24-hour format
    });
  }

  /**
   * 格式化日期顯示
   */
  formatDate(dateInput: any): string {
    if (!dateInput) return '';

    let date: Date;

    if (typeof dateInput === 'number') {
      // 判斷是否為秒級時間戳
      if (dateInput < 10000000000) {
        date = new Date(dateInput * 1000);
      } else {
        date = new Date(dateInput);
      }
    } else if (typeof dateInput === 'string') {
      // Check if the string is in date format (YYYY-MM-DD, YYYY/MM/DD, etc.)
      const dateFormatRegex = /^\d{4}[-/]\d{1,2}[-/]\d{1,2}/;
      if (dateFormatRegex.test(dateInput)) {
        date = new Date(dateInput);
      } else {
        // Only process as timestamp if it's a pure numeric string
        const numericValue = parseInt(dateInput);
        if (!isNaN(numericValue) && /^\d+$/.test(dateInput.trim())) {
          if (numericValue < 10000000000) {
            date = new Date(numericValue * 1000);
          } else {
            date = new Date(numericValue);
          }
        } else {
          date = new Date(dateInput);
        }
      }
    } else if (dateInput instanceof Date) {
      date = dateInput;
    } else {
      return '';
    }

    // 檢查日期是否有效
    if (isNaN(date.getTime())) {
      return '';
    }

    return date.toLocaleDateString('zh-TW', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    });
  }

  /**
   * 取得欄位的值
   */
  getFieldValue(fieldName: string): any {
    if (!this.formData) return null;

    // 直接檢查欄位值
    if (this.formData.hasOwnProperty(fieldName)) {
      const value = this.formData[fieldName];

      // 如果是物件且有 value 屬性，則取得 value
      if (value && typeof value === 'object' && value.hasOwnProperty('value')) {
        return value.value;
      }

      // 否則直接返回值
      return value;
    }

    return null;
  }

  /**
   * 取得顯示文字
   */
  getDisplayValue(value: any): string {
    if (value === null || value === undefined) return '';

    // 處理經過轉換的複雜元件資料
    if (typeof value === 'object' && value.type) {
      switch (value.type) {
        case 'file-upload-manager':
        case 'required-document-upload':
        case 'avatar-upload':
        case 'signature':
        case 'meeting-signature-collection':
        case 'dynamic-api-table':
        case 'auto-numbering':
        case 'risk-assessment-matrix':
        case 'audio-recorder':
        case 'video-player':
          return value.summary || value.value || '';
        
        case 'fill-in':
        case 'subform-relation':
        case 'tags':
        case 'location':
          return value.summary || value.value || '';
        
        case 'personnel-selector':
        case 'department-selector':
        case 'dynamic-api-select':
        case 'approval-flow':
          return value.value || '';
        
        case 'timestamp':
          // 處理時間戳記顯示
          // console.log('Form-display timestamp value:', value);
          if (value.summary) {
            return value.summary;
          } else if (value.value) {
            // 處理雙重巢狀結構
            let timestamp: number;
            // console.log('value.value type:', typeof value.value, 'value:', value.value);
            if (typeof value.value === 'object' && value.value.value) {
              timestamp = value.value.value;
              // console.log('Using nested value:', timestamp);
            } else {
              timestamp = typeof value.value === 'number' ? value.value : Number(value.value);
              // console.log('Using direct value:', timestamp);
            }
            
            if (!isNaN(timestamp)) {
              const date = new Date(timestamp);
              // 格式化為 yyyy-mm-dd HH:mm:ss
              const year = date.getFullYear();
              const month = String(date.getMonth() + 1).padStart(2, '0');
              const day = String(date.getDate()).padStart(2, '0');
              const hours = String(date.getHours()).padStart(2, '0');
              const minutes = String(date.getMinutes()).padStart(2, '0');
              const seconds = String(date.getSeconds()).padStart(2, '0');
              return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
            }
          }
          return '無效時間';
        
        case 'date-range':
          // 處理日期範圍顯示
          if (value.summary) {
            return value.summary;
          } else if (value.value) {
            return this.formatDateRange(value.value);
          }
          return '';
        
        default:
          // 如果有 summary 屬性，優先使用
          if (value.summary) return value.summary;
          break;
      }
    }

    // 如果是物件
    if (typeof value === 'object') {
      // 如果有 value 屬性，嘗試顯示該值
      if (value.hasOwnProperty('value')) {
        return this.getDisplayValue(value.value);
      }

      // 如果是陣列，嘗試顯示為字串
      if (Array.isArray(value)) {
        return value.map(item => this.getDisplayValue(item)).join(', ');
      }

      // 其他物件，嘗試找到可顯示的屬性
      if (value.name) return value.name;
      if (value.label) return value.label;
      if (value.title) return value.title;
      if (value.text) return value.text;

      // 最後才使用 JSON.stringify
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return '[Object]';
      }
    }

    return String(value);
  }

  /**
   * 解析照片資料
   */
  parsePhotos(photosData: any): any[] {
    if (!photosData) return [];

    if (typeof photosData === 'string') {
      try {
        return JSON.parse(photosData);
      } catch {
        return [];
      }
    }

    if (Array.isArray(photosData)) {
      return photosData;
    }

    return [];
  }

  /**
   * 取得Google Maps連結
   */
  getGoogleMapsUrl(location: any): string {
    if (!location) return '#';

    let lat, lng;

    if (typeof location === 'string') {
      try {
        const parsed = JSON.parse(location);
        lat = parsed.lat || parsed.latitude;
        lng = parsed.lng || parsed.longitude;
      } catch {
        return '#';
      }
    } else if (typeof location === 'object') {
      lat = location.lat || location.latitude;
      lng = location.lng || location.longitude;
    }

    if (lat && lng) {
      return `https://www.google.com/maps?q=${lat},${lng}`;
    }

    return '#';
  }

  /**
   * 取得多選項目的選中文字
   */
  getMultiCheckboxSelectedText(options: { option: string; selected: boolean; textValue?: string }[] | any): string {
    // console.log("=== getMultiCheckboxSelectedText 開始 ===");
    // console.log("輸入的 options:", options);

    // 檢查 options 是否為陣列
    if (!Array.isArray(options)) {
      // 如果不是陣列，嘗試解析為陣列
      if (typeof options === 'string') {
        try {
          const parsed = JSON.parse(options);
          if (Array.isArray(parsed)) {
            options = parsed;
          } else {
            return options; // 如果解析後不是陣列，直接返回字串值
          }
        } catch {
          return options; // 解析失敗，直接返回原值
        }
      } else if (options === null || options === undefined) {
        return ''; // 空值返回空字串
      } else {
        return String(options); // 其他類型轉為字串返回
      }
    }

    // 找出所有被選中的選項
    const selectedOptions = options.filter((option: any) => option && option.selected === true);

    // 將選中的選項轉換為顯示文字
    const displayTexts = selectedOptions.map((option: any) => {
      // console.log("處理選項:", option);

      // 處理新的 key/value 資料結構
      let text;

      if (option.option && typeof option.option === 'object' && option.option.value) {
        // 新格式：option 是 { key, value } 物件
        text = option.option.value;
        // console.log("使用 option.option.value:", text);
      } else if (option.name && typeof option.name === 'object' && option.name.value) {
        // 新格式：name 是 { key, value } 物件
        text = option.name.value;
        // console.log("使用 option.name.value:", text);
      } else {
        // 舊格式相容性
        text = option.option || option.name || String(option);
        // console.log("使用舊格式:", text);
      }

      // 處理底線替換邏輯
      if (option.textValue && option.textValue.trim() !== '') {
        // 如果選項文字中包含底線，且有textValue，則用textValue替換所有底線
        if (text.includes('_')) {
          text = text.replace(/_+/g, " [" + option.textValue + "] ");
        } else {
          // 如果沒有底線但有textValue，則按原邏輯加上文字值（適用於"其他"等選項）
          text += `: ${option.textValue}`;
        }
      }

      // console.log("最終文字:", text);
      return text;
    });

    const result = displayTexts.join(', ');
    // console.log("最終結果:", result);
    // console.log("=== getMultiCheckboxSelectedText 結束 ===");
    return result;
  }

  /**
   * 轉換為簽核流程步驟
   */
  convertToApprovalFlowSteps(field: FormFieldConfig): any[] {
    // console.log("=== convertToApprovalFlowSteps 開始 ===");
    // console.log("field:", field);

    const approvalFlowData = this.getApprovalFlowData(field);
    // console.log("從 getApprovalFlowData 取得的資料:", approvalFlowData);

    if (!approvalFlowData || !Array.isArray(approvalFlowData)) {
      // console.log("簽核流程資料無效，返回空陣列");
      return [];
    }

    // 將簽核流程資料轉換為 ApprovalStep 格式，並設置適當的狀態
    const convertedSteps = approvalFlowData.map((step: any, index: number) => {
      // console.log(`處理步驟 ${index}:`, step);

      // 根據簽核流程邏輯設置狀態：
      // - 第一個步驟通常是 in_progress（黃色，可點擊）
      // - 後續步驟是 pending（灰色，不可點擊）
      // - 如果有現有狀態，優先使用現有狀態
      let status = step.status;
      if (!status) {
        // 如果沒有現有狀態，第一個步驟設為 in_progress，其餘為 pending
        status = index === 0 ? 'in_progress' : 'pending';
      }
      // console.log(`步驟 ${index} 狀態設為:`, status);

      const convertedStep = {
        stepDefinitionId: step.stepDefinitionId || step.id || `step-${index}`,
        level: step.level || (index + 1).toString(),
        stepName: step.stepName || step.name || `步驟 ${index + 1}`,
        approverName: step.approverList ? step.approverList.map((a: any) => a.name).join(', ') : '',
        approverNames: step.approverList ? step.approverList.map((a: any) => a.name) : [],
        approverIds: step.approverList ? step.approverList.map((a: any) => a.id) : [],
        status: status,
        comment: step.comment || '',
        actionDate: step.actionDate || '',
        clickable: true,
        stepIndex: index
      };

      // console.log(`轉換後的步驟 ${index}:`, convertedStep);
      return convertedStep;
    });

    // console.log("最終轉換結果:", convertedSteps);
    // console.log("=== convertToApprovalFlowSteps 結束 ===");
    return convertedSteps;
  }

  /**
   * 取得簽核流程資料
   */
  getApprovalFlowData(field: FormFieldConfig): any[] {
    const value = this.getFieldValue(field.name);
    if (!value) return [];

    try {
      // 處理簽核流程資料的多種可能結構 - 與 showlist 的 parseApprovalFlow 方法保持一致
      let approvalData: any = value;

      // 如果已經是陣列，直接返回
      if (Array.isArray(value)) {
        return value;
      }

      // 如果是字串，嘗試解析 JSON
      if (typeof value === 'string') {
        try {
          approvalData = JSON.parse(value);
        } catch {
          return [];
        }
      }

      // 如果是物件且有 value 屬性 - 模仿 showlist 的處理邏輯
      if (approvalData.value) {
        const innerData = approvalData.value;

        // 檢查雙層巢狀結構 (innerData.value?.steps) - 這是關鍵修復
        if (innerData.value?.steps) {
          return innerData.value.steps;
        }
        // 檢查單層結構 (innerData.steps)
        else if (innerData.steps) {
          return innerData.steps;
        }
        // 如果 innerData 直接是陣列
        else if (Array.isArray(innerData)) {
          return innerData;
        }
        // 如果 approvalData.value 是字串，嘗試解析
        else if (typeof innerData === 'string') {
          return JSON.parse(innerData);
        }
      }

      return [];
    } catch (error) {
      console.warn('解析簽核流程資料失敗:', error);
      return [];
    }
  }

  /**
   * Convert approval-personnel-selector field to approval flow steps format
   */
  convertToApprovalPersonnelSteps(field: FormFieldConfig): any[] {
    console.log("=== convertToApprovalPersonnelSteps starting ===");
    console.log("field:", field);
    
    const approvalPersonnelData = this.getApprovalPersonnelData(field);
    console.log("Data retrieved from getApprovalPersonnelData:", approvalPersonnelData);
    
    if (!approvalPersonnelData || !Array.isArray(approvalPersonnelData)) {
      console.log("Invalid approval personnel data, returning empty array");
      return [];
    }

    // Convert approval personnel data to ApprovalStep format with appropriate status
    const convertedSteps = approvalPersonnelData.map((step: any, index: number) => {
      console.log(`Processing step ${index}:`, step);
      
      // Set status based on approval flow logic:
      // - First step is usually in_progress (yellow, clickable)
      // - Subsequent steps are pending (gray, not clickable)
      // - Use existing status if available
      let status = step.status;
      if (!status) {
        // If no existing status, set first step to in_progress, others to pending
        status = index === 0 ? 'in_progress' : 'pending';
      }
      console.log(`Step ${index} status set to:`, status);

      const convertedStep = {
        stepDefinitionId: step.stepDefinitionId || step.id || `personnel-step-${index}`,
        level: step.level || (index + 1).toString(),
        stepName: step.stepName || step.name || `Step ${index + 1}`,
        approverName: step.approverList ? step.approverList.map((a: any) => a.name).join(', ') : '',
        approverNames: step.approverList ? step.approverList.map((a: any) => a.name) : [],
        approverIds: step.approverList ? step.approverList.map((a: any) => a.id) : [],
        status: status,
        comment: step.comment || '',
        actionDate: step.actionDate || '',
        clickable: true,
        stepIndex: index
      };
      
      console.log(`Converted step ${index}:`, convertedStep);
      return convertedStep;
    });
    
    console.log("Final conversion result:", convertedSteps);
    console.log("=== convertToApprovalPersonnelSteps ending ===");
    return convertedSteps;
  }

  /**
   * Get approval personnel data
   */
  getApprovalPersonnelData(field: FormFieldConfig): any[] {
    const value = this.getFieldValue(field.name);
    if (!value) return [];

    try {
      // Handle multiple possible structures of approval personnel data
      let approvalData: any = value;

      // If already an array, return directly
      if (Array.isArray(value)) {
        return value;
      }

      // If string, try to parse JSON
      if (typeof value === 'string') {
        try {
          approvalData = JSON.parse(value);
        } catch {
          return [];
        }
      }

      // Handle object structure with value property
      if (approvalData.value) {
        const innerData = approvalData.value;

        // Check for nested structure (innerData.value?.steps)
        if (innerData.value?.steps) {
          return innerData.value.steps;
        }
        // Check for single-level structure (innerData.steps)
        else if (innerData.steps) {
          return innerData.steps;
        }
        // If innerData is directly an array
        else if (Array.isArray(innerData)) {
          return innerData;
        }
        // If approvalData.value is a string, try to parse
        else if (typeof innerData === 'string') {
          return JSON.parse(innerData);
        }
      }

      // If approvalData has steps property directly
      if (approvalData.steps) {
        return approvalData.steps;
      }

      // If approvalData is directly an array
      if (Array.isArray(approvalData)) {
        return approvalData;
      }

      return [];
    } catch (error) {
      console.warn('Failed to parse approval personnel data:', error);
      return [];
    }
  }

  /**
   * 檢查是否有檔案
   */
  hasFiles(field: FormFieldConfig): boolean {
    const value = this.getFieldValue(field.name);
    if (!value) return false;

    // 檢查不同的檔案資料結構
    if (value.files && Array.isArray(value.files) && value.files.length > 0) {
      return true;
    }

    // 檢查是否直接是檔案陣列
    if (Array.isArray(value) && value.length > 0) {
      return true;
    }

    // 檢查欄位配置中的檔案
    if (field.files && Array.isArray(field.files) && field.files.length > 0) {
      return true;
    }

    return false;
  }

  /**
   * 取得動態API表格資料筆數
   */
  getDynamicApiTableCount(value: any): number {
    if (!value) {
      return 0;
    }

    if (Array.isArray(value)) {
      return value.length;
    }

    return 0;
  }


  /**
   * 取得欄位顯示名稱
   */
  getColumnDisplayName(column: string): string {
    // 這裡可以根據需要進行欄位名稱的轉換
    // 例如：將英文欄位名轉換為中文顯示名稱
    return column;
  }

  /**
   * 取得動態API表格資料（用於直接顯示）
   */
  getDynamicApiTableData(value: any): any[] {
    if (!value) {
      return [];
    }
    if (Array.isArray(value)) {
      return value;
    }
    // 如果是物件格式且包含陣列資料
    if (typeof value === 'object' && value.value && Array.isArray(value.value)) {
      return value.value;
    }
    return [];
  }

  /**
   * 取得動態API表格欄位（用於直接顯示）
   */
  getDynamicApiTableColumns(value: any): string[] {
    const data = this.getDynamicApiTableData(value);
    if (data.length > 0) {
      return Object.keys(data[0]);
    }
    return [];
  }

  /**
   * 取得表格儲存格的顯示值（處理物件型態）
   */
  getDisplayValueForTableCell(cellValue: any): string {
    if (cellValue === null || cellValue === undefined) {
      return '';
    }
    
    // 如果是物件且有 value 屬性，顯示 value
    if (typeof cellValue === 'object' && cellValue.value !== undefined) {
      return cellValue.value;
    }
    
    // 如果是物件且有 key 和 value 屬性，顯示 value
    if (typeof cellValue === 'object' && cellValue.key && cellValue.value !== undefined) {
      return cellValue.value;
    }
    
    // 如果是物件但沒有 value 屬性，嘗試轉為 JSON 字串
    if (typeof cellValue === 'object') {
      try {
        return JSON.stringify(cellValue);
      } catch {
        return '[Object]';
      }
    }
    
    // 其他情況直接轉字串
    return String(cellValue);
  }

  /**
   * 格式化日期範圍顯示
   */
  formatDateRange(dateRange: any): string {
    if (!dateRange || !dateRange.startDate || !dateRange.endDate) {
      return '';
    }
    try {
      const startDate = new Date(dateRange.startDate);
      const endDate = new Date(dateRange.endDate);
      
      // 檢查是否包含時間 (如果字串包含 'T' 表示有時間)
      const hasTime = (typeof dateRange.startDate === 'string' && dateRange.startDate.includes('T')) || 
                     (typeof dateRange.endDate === 'string' && dateRange.endDate.includes('T'));
      
      if (hasTime) {
        // 如果包含時間，格式化為 yyyy-mm-dd HH:MM
        const startFormatted = this.formatDateToYMD(startDate, true);
        const endFormatted = this.formatDateToYMD(endDate, true);
        return `${startFormatted} ~ ${endFormatted}`;
      } else {
        // 如果只有日期，格式化為 yyyy-mm-dd
        const startFormatted = this.formatDateToYMD(startDate, false);
        const endFormatted = this.formatDateToYMD(endDate, false);
        return `${startFormatted} ~ ${endFormatted}`;
      }
    } catch (error) {
      console.error('格式化日期範圍失敗:', error);
      return `${dateRange.startDate} ~ ${dateRange.endDate}`;
    }
  }

  /**
   * 格式化日期為 yyyy-mm-dd 或 yyyy-mm-dd HH:MM 格式
   */
  private formatDateToYMD(date: Date, includeTime: boolean = false): string {
    // 加強日期驗證邏輯
    if (!date || date === null || date === undefined) {
      console.warn('formatDateToYMD: 日期為空或未定義:', date);
      return '';
    }
    
    // 確保是Date物件且有getTime方法
    if (!(date instanceof Date) || typeof date.getTime !== 'function') {
      console.warn('formatDateToYMD: 無效的日期物件類型:', typeof date, date);
      return '';
    }
    
    // 檢查日期是否有效
    if (isNaN(date.getTime())) {
      console.warn('formatDateToYMD: 無效的日期值:', date);
      return '';
    }
    
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    
    if (includeTime) {
      const hours = String(date.getHours()).padStart(2, '0');
      const minutes = String(date.getMinutes()).padStart(2, '0');
      return `${year}-${month}-${day} ${hours}:${minutes}`;
    } else {
      return `${year}-${month}-${day}`;
    }
  }

  /**
   * 取得會議簽名收集狀態文字
   */
  getMeetingSignatureCollectionText(value: any): string {
    if (!value) return '';
    
    // 處理轉換後的格式
    if (typeof value === 'object' && value.type === 'meeting-signature-collection') {
      return value.summary || '簽名收集';
    }
    
    if (typeof value === 'object') {
      // 如果是物件，嘗試提取狀態資訊
      if (value.status) {
        return value.status;
      }
      if (value.participants && Array.isArray(value.participants)) {
        const signed = value.participants.filter((p: any) => p.signed).length;
        const total = value.participants.length;
        return `${signed}/${total} ${this.i18nService.translate('form.approval.signed')}`;
      }
      // 其他情況返回 JSON 字串
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return '[Meeting Signature Collection]';
      }
    }
    
    return String(value);
  }

  /**
   * 取得風險評估矩陣文字
   */
  getRiskAssessmentMatrixText(value: any): string {
    if (!value) return '';
    
    // 處理轉換後的格式
    if (typeof value === 'object' && value.type === 'risk-assessment-matrix') {
      return value.summary || '風險評估';
    }
    
    if (typeof value === 'object') {
      // 如果是物件，嘗試提取風險評估結果
      if (value.riskLevel) {
        return `風險等級: ${value.riskLevel}`;
      }
      if (value.score !== undefined) {
        return `評估分數: ${value.score}`;
      }
      if (value.result) {
        return value.result;
      }
      // 其他情況返回 JSON 字串
      try {
        return JSON.stringify(value, null, 2);
      } catch {
        return '[Risk Assessment Matrix]';
      }
    }
    
    return String(value);
  }

  /**
   * 取得檔案資料 - 使用與 showlist 相同的邏輯
   */
  getFiles(field: FormFieldConfig): any[] {
    const value = this.getFieldValue(field.name);
    
    console.log("form-display getFiles 方法被呼叫");
    console.log("field.name:", field.name);
    console.log("value:", value);

    // 如果是 file-upload-manager 類型，使用特殊處理邏輯
    if (field.type === 'file-upload-manager') {
      return this.getFileManagerFiles(value);
    }

    // 原有的檔案處理邏輯（用於其他檔案類型）
    if (value) {
      if (value.files && Array.isArray(value.files)) {
        return value.files;
      }

      if (Array.isArray(value)) {
        return value;
      }
    }

    // 檢查欄位配置中的檔案
    if (field.files && Array.isArray(field.files)) {
      return field.files;
    }

    return [];
  }

  /**
   * 取得檔案管理器檔案（與 showlist 的 getFileManagerFiles 相同邏輯）
   */
  getFileManagerFiles(documentValue: any): any[] {
    console.log("getFileManagerFiles 被呼叫，documentValue:", documentValue);
    
    if (!documentValue) return [];
    
    // 格式1：優先檢查 originalValue.files（新格式中包含完整資訊）
    if (documentValue.originalValue && documentValue.originalValue.files && Array.isArray(documentValue.originalValue.files)) {
      const originalFiles = documentValue.originalValue.files;
      console.log("找到 originalValue.files:", originalFiles);
      
      // 檢查第一個檔案是否有完整的 blobName 或 viewUrl
      if (originalFiles.length > 0 && originalFiles[0] && (originalFiles[0].blobName || originalFiles[0].viewUrl)) {
        console.log('使用 originalValue.files，檔案有完整資訊:', originalFiles[0]);
        return originalFiles;
      }
    }
    
    // 格式2：如果 originalValue 不存在或不完整，使用 documentValue.files（舊格式）
    if (documentValue.files && Array.isArray(documentValue.files)) {
      const files = documentValue.files;
      console.log("找到 documentValue.files:", files);
      
      // 檢查第一個檔案是否有完整的 blobName 或 viewUrl
      if (files.length > 0 && files[0] && (files[0].blobName || files[0].viewUrl)) {
        console.log('使用 documentValue.files，檔案有完整資訊:', files[0]);
        return files;
      } else if (files.length > 0 && files[0]) {
        console.log('documentValue.files 中的檔案缺少 blobName/viewUrl:', files[0]);
        // 如果檔案缺少關鍵資訊但仍然存在，仍然返回（讓 viewFile 方法處理）
        return files;
      }
    }
    
    console.log("未找到有效的檔案資料");
    return [];
  }

  /**
   * 檢視檔案
   */
  viewFile(file: any): void {
    console.log("form-display viewFile 被呼叫，檔案資訊:", file);
    
    if (!file) {
      console.warn('檔案物件為空');
      return;
    }

    // 檢查 viewUrl 是否需要重新產生（過期或缺少 Content-Type）
    const isUrlPotentiallyExpired = (url: string): boolean => {
      if (!url.includes('se=')) return false; // 沒有過期時間參數
      
      try {
        const urlObj = new URL(url);
        
        // 檢查是否缺少 Content-Type 參數（對於 PDF 檔案很重要）
        const rsct = urlObj.searchParams.get('rsct');
        if (!rsct && url.toLowerCase().includes('.pdf')) {
          console.log('PDF 檔案缺少 Content-Type 參數，需要重新產生 URL');
          return true;
        }
        
        // 檢查是否過期
        const se = urlObj.searchParams.get('se'); // 取得過期時間
        if (se) {
          const expiryTime = new Date(se);
          const now = new Date();
          // 如果還有5分鐘就過期或已過期，重新產生
          return (expiryTime.getTime() - now.getTime()) < 5 * 60 * 1000;
        }
      } catch (error) {
        console.warn('無法解析 URL 過期時間:', error);
      }
      return false;
    };

    // 優先順序1：檢查是否有 viewUrl 且未過期
    if (file.viewUrl && !isUrlPotentiallyExpired(file.viewUrl)) {
      console.log('使用現有的 viewUrl 開啟檔案 (未過期):', file.viewUrl);
      console.log('URL 是否包含 inline 參數:', file.viewUrl.includes('rscd=inline'));
      
      // 嘗試多種方式開啟檔案
      this.openFileInNewTab(file.viewUrl);
      return;
    }

    // 如果 viewUrl 已過期，記錄並繼續到重新產生邏輯
    if (file.viewUrl && isUrlPotentiallyExpired(file.viewUrl)) {
      console.log('viewUrl 已過期，將重新產生:', file.viewUrl);
    }

    // 優先順序2：如果有 blobName，重新產生 viewUrl
    if (file && file.blobName) {
      console.log('使用 blobName 重新產生 viewUrl:', file.blobName);
      // 使用 FileUploadService 產生瀏覽連結
      this.fileUploadService.generateViewUrl(file.blobName).subscribe({
        next: (response: any) => {
          if (response.success && response.data.viewUrl) {
            console.log('重新產生的 viewUrl:', response.data.viewUrl);
            console.log('新 URL 是否包含 inline 參數:', response.data.viewUrl.includes('rscd=inline'));
            
            // 更新檔案物件中的 URL
            file.viewUrl = response.data.viewUrl;
            file.previewUrl = response.data.viewUrl;
            
            // 使用新的開啟方式
            this.openFileInNewTab(response.data.viewUrl);
          } else {
            console.error('重新產生檔案檢視 URL 失敗：無有效 URL');
            this.tryOriginalViewUrl(file);
          }
        },
        error: (error) => {
          console.error('重新產生檔案檢視 URL 失敗:', error);
          this.tryOriginalViewUrl(file);
        }
      });
      return;
    }

    // 優先順序3：備用方案，使用其他可用的 URL
    this.tryOriginalViewUrl(file);
  }

  /**
   * 嘗試使用原始檢視 URL
   */
  private tryOriginalViewUrl(file: any): void {
    // 優先使用 viewUrl（通常包含 rscd=inline 參數，用於瀏覽器內顯示）
    // 然後才使用 downloadUrl（可能會直接下載）
    const viewUrl = file.viewUrl || file.previewUrl || file.url || file.fileUrl || file.src || file.href || file.downloadUrl;
    
    if (viewUrl) {
      console.log('使用備用 URL 開啟檔案:', viewUrl);
      
      // 檢查 URL 是否包含 inline 參數
      if (viewUrl.includes('rscd=inline')) {
        console.log('URL 包含 inline 參數，應該在瀏覽器中顯示');
      } else {
        console.log('URL 不包含 inline 參數，可能會觸發下載');
      }
      
      // 使用新的開啟方式
      this.openFileInNewTab(viewUrl);
    } else {
      console.error('沒有可用的檢視 URL');
      alert(this.i18nService.translate('form.display.file_link_unavailable') || '檔案連結已失效，請聯繫管理員');
    }
  }

  /**
   * 檢視圖片全尺寸
   */
  viewImageFullSize(imageUrl: string): void {
    if (imageUrl) {
      window.open(imageUrl, '_blank');
    }
  }

  /**
   * 簽核完成處理
   */
  async onApprovalComplete(event: any): Promise<void> {
    console.log('Approval complete event in display mode:', event);

    try {
      // 重新載入表單資料以更新簽核狀態
      if (this.formid && this.recordId) {
        console.log('簽核完成，重新載入表單資料...');
        await this.loadFormData();
        console.log('表單資料重新載入完成');

        // 記錄簽核完成狀態（供父元件在關閉視窗時查詢）
        this.hasApprovalCompleted = true;
        this.latestApprovalData = {
          recordId: this.recordId,
          formId: this.formid,
          updatedData: this.formData
        };
        console.log('已記錄簽核完成狀態，等待視窗關閉時通知父元件');

        // 發出承認完成事件給父元件
        this.approvalComplete.emit({
          ...event,
          recordId: this.recordId,
          formId: this.formid,
          updatedData: this.formData
        });
      } else {
        console.warn('缺少 formid 或 recordId，無法重新載入資料');
        // 即使沒有formid和recordId，也發出事件
        this.approvalComplete.emit(event);
      }
    } catch (error) {
      console.error('重新載入表單資料失敗:', error);
      // 即使發生錯誤，也發出事件
      this.approvalComplete.emit(event);
    }
  }

  /**
   * 檢查是否有簽核完成（供父元件查詢）
   */
  public getApprovalStatus(): { hasCompleted: boolean; data: any } {
    return {
      hasCompleted: this.hasApprovalCompleted,
      data: this.latestApprovalData
    };
  }

  /**
   * 重置簽核狀態（供父元件在處理完後呼叫）
   */
  public resetApprovalStatus(): void {
    this.hasApprovalCompleted = false;
    this.latestApprovalData = null;
  }

  /**
   * 取得當前使用者ID
   */
  getCurrentUserId(): string {
    const userInfo = this.authService.getUserInfo();
    return userInfo.userid || '';
  }

  /**
   * 取得必要文件檔案（與 showlist 的 getRequiredDocumentFile 相同邏輯）
   */
  getRequiredDocumentFile(documentValue: any): any {
    if (!documentValue) return null;
    
    
    // 格式1：直接在 documentValue.file 中（舊的單一檔案格式）
    if (documentValue.file) {
      return documentValue.file;
    }
    
    // 格式2：在 documentValue.originalValue.file 中（新格式）
    if (documentValue.originalValue && documentValue.originalValue.file) {
      return documentValue.originalValue.file;
    }
    
    // 格式3：深層巢狀 - documentValue.originalValue.originalValue.file
    if (documentValue.originalValue && 
        documentValue.originalValue.originalValue && 
        documentValue.originalValue.originalValue.file) {
      return documentValue.originalValue.originalValue.file;
    }
    
    console.log('未找到必要文件檔案資料');
    return null;
  }

  /**
   * 取得必要文件 AI 解析結果（與 showlist 的 getRequiredDocumentAIResponse 相同邏輯）
   */
  getRequiredDocumentAIResponse(documentValue: any): string {
    if (!documentValue) return '';
    
    // 格式1：直接在 documentValue.aiParseResult 中
    if (documentValue.aiParseResult) {
      // 優先使用 response 欄位（新格式）
      if (documentValue.aiParseResult.response) {
        return documentValue.aiParseResult.response;
      }
      // 向下相容：使用 AI_Response 欄位（舊格式）
      if (documentValue.aiParseResult.AI_Response) {
        return documentValue.aiParseResult.AI_Response;
      }
    }
    
    // 格式2：在 documentValue.originalValue.aiParseResult 中
    if (documentValue.originalValue && documentValue.originalValue.aiParseResult) {
      // 優先使用 response 欄位（新格式）
      if (documentValue.originalValue.aiParseResult.response) {
        return documentValue.originalValue.aiParseResult.response;
      }
      // 向下相容：使用 AI_Response 欄位（舊格式）
      if (documentValue.originalValue.aiParseResult.AI_Response) {
        return documentValue.originalValue.aiParseResult.AI_Response;
      }
    }
    
    return '';
  }

  /**
   * 檢查是否有必要文件
   */
  hasRequiredDocument(documentValue: any): boolean {
    return this.getRequiredDocumentFile(documentValue) !== null;
  }

  /**
   * 在新分頁中開啟檔案 - 嘗試多種方式確保檔案顯示而非下載
   */
  private openFileInNewTab(url: string): void {
    console.log('openFileInNewTab 被呼叫，URL:', url);
    
    // 方法1：使用 iframe 方式（對 PDF 和圖片很有效）
    const fileExtension = this.getFileExtension(url);
    console.log('檔案副檔名:', fileExtension);
    
    if (fileExtension === 'pdf' || fileExtension === 'jpg' || fileExtension === 'jpeg' || fileExtension === 'png' || fileExtension === 'gif') {
      console.log('使用 iframe 方式開啟檔案');
      this.createViewerWindow(url);
    } else {
      console.log('使用傳統 window.open 方式');
      // 方法2：傳統方式作為備用
      const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
      if (!newWindow) {
        console.warn('無法開啟新視窗，嘗試其他方式');
        window.location.href = url;
      }
    }
  }

  /**
   * 取得檔案副檔名
   */
  private getFileExtension(url: string): string {
    try {
      // 從 URL 中提取檔案名稱（去除查詢參數）
      const urlWithoutQuery = url.split('?')[0];
      const fileName = urlWithoutQuery.split('/').pop() || '';
      const extension = fileName.split('.').pop()?.toLowerCase() || '';
      return extension;
    } catch (error) {
      console.warn('無法解析檔案副檔名:', error);
      return '';
    }
  }

  /**
   * 建立檔案檢視器視窗
   */
  private createViewerWindow(url: string): void {
    const fileExtension = this.getFileExtension(url);
    
    // 對於 PDF，直接在新分頁開啟，不使用 iframe
    if (fileExtension === 'pdf') {
      console.log('PDF 檔案直接在新分頁開啟');
      const newWindow = window.open(url, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
      
      if (!newWindow) {
        console.warn('無法開啟新視窗，嘗試其他方式');
        window.location.href = url;
      }
      return;
    }
    
    // 對於圖片檔案，使用 iframe 檢視器
    const newWindow = window.open('', '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
    
    if (newWindow) {
      newWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>檔案檢視器</title>
          <style>
            body { 
              margin: 0; 
              padding: 20px; 
              background: #f0f0f0; 
              font-family: Arial, sans-serif;
              text-align: center;
            }
            .image-container {
              background: white;
              padding: 20px;
              border-radius: 8px;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
              display: inline-block;
              max-width: 100%;
            }
            img { 
              max-width: 100%; 
              max-height: 80vh; 
              border: none;
              border-radius: 4px;
            }
            .toolbar {
              margin-bottom: 20px;
              padding: 10px;
              background: white;
              border-radius: 8px;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            }
            .btn {
              background: #007bff;
              color: white;
              border: none;
              padding: 8px 16px;
              border-radius: 4px;
              margin: 0 5px;
              cursor: pointer;
              text-decoration: none;
              display: inline-block;
            }
            .btn:hover {
              background: #0056b3;
            }
            .error { 
              padding: 20px; 
              text-align: center; 
              background: white;
              border-radius: 8px;
              box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            }
          </style>
        </head>
        <body>
          <div class="toolbar">
            <a href="${url}" target="_blank" class="btn">在新分頁開啟原始檔案</a>
            <button onclick="window.close()" class="btn">關閉視窗</button>
          </div>
          <div class="image-container">
            <img src="${url}" onerror="this.parentElement.style.display='none'; document.querySelector('.error').style.display='block';" alt="檔案預覽">
          </div>
          <div class="error" style="display: none;">
            <h3>無法顯示檔案</h3>
            <p><a href="${url}" target="_blank" class="btn">點此直接開啟檔案</a></p>
          </div>
        </body>
        </html>
      `);
      newWindow.document.close();
    } else {
      console.warn('無法建立新視窗，使用備用方式');
      window.open(url, '_blank');
    }
  }

  /**
   * 檢查簽名資料是否有效
   */
  hasValidSignatureData(value: any): boolean {
    if (!value) return false;
    
    // 檢查是否為物件且有必要的屬性
    if (typeof value === 'object') {
      // 檢查是否有簽名圖片資料
      return this.getSignatureImageData(value) !== null;
    }
    
    return false;
  }

  /**
   * 取得簽名圖片資料
   */
  getSignatureImageData(signature: any): string | null {
    if (!signature) return null;
    
    // 處理巢狀格式：originalValue.imageData
    if (signature.originalValue && signature.originalValue.imageData) {
      return signature.originalValue.imageData;
    }
    
    // 如果是字串格式的 base64 資料（舊格式）
    if (typeof signature === 'string' && signature.startsWith('data:image/')) {
      return signature;
    }
    
    // 如果是物件格式包含 imageData（中等格式）
    if (typeof signature === 'object' && signature.imageData) {
      return signature.imageData;
    }
    
    return null;
  }

  /**
   * 取得簽名摘要
   */
  getSignatureSummary(value: any): string {
    if (!value) {
      return '未簽名';
    }
    
    // 優先取得 summary
    if (value.summary) {
      return value.summary;
    }
    
    // 根據 hasSignature 判斷
    if (value.hasSignature === true) {
      return this.i18nService.translate('form.approval.signed');
    } else if (value.hasSignature === false) {
      return this.i18nService.translate('form.approval.unsigned');
    }
    
    // 如果有圖片資料則認為已簽名
    if (this.getSignatureImageData(value)) {
      return this.i18nService.translate('form.approval.signed');
    }
    
    return this.i18nService.translate('form.approval.unsigned');
  }

  /**
   * 取得簽名時間
   */
  getSignatureDate(value: any): string | null {
    if (!value) {
      return null;
    }
    
    // 處理巢狀格式中的時間
    if (value.originalValue && value.originalValue.signedAt) {
      return this.formatSignedDate(value.originalValue.signedAt);
    }
    
    // 處理直接格式中的時間
    if (value.signedAt) {
      return this.formatSignedDate(value.signedAt);
    }
    
    // 處理 signerInfo 中的時間
    if (value.signerInfo && value.signerInfo.signedAt) {
      return this.formatSignedDate(value.signerInfo.signedAt);
    }
    
    return null;
  }

  /**
   * 格式化簽名日期
   */
  private formatSignedDate(signedAt: any): string {
    try {
      let date: Date;
      
      if (typeof signedAt === 'string') {
        date = new Date(signedAt);
      } else if (typeof signedAt === 'number') {
        // 檢查是否為 Unix timestamp（秒數格式）
        if (signedAt < 10000000000) {
          date = new Date(signedAt * 1000);
        } else {
          date = new Date(signedAt);
        }
      } else {
        return '';
      }
      
      // 檢查日期是否有效
      if (isNaN(date.getTime())) {
        return '';
      }
      
      return date.toLocaleDateString('zh-TW', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch (error) {
      console.warn('格式化簽名日期失敗:', error);
      return '';
    }
  }

  /**
   * 產生簽名的 tooltip 文字
   */
  getSignatureTooltip(signatureValue: any): string {
    if (!signatureValue || !signatureValue.signerInfo) {
      return '簽名';
    }
    
    const signerInfo = signatureValue.signerInfo;
    let tooltip = `簽名者: ${signerInfo.username || '未知'}`;
    
    if (signerInfo.signedAt) {
      const signedDate = this.formatSignedDate(signerInfo.signedAt);
      tooltip += `\n簽名時間: ${signedDate}`;
    }
    
    return tooltip;
  }


  /**
   * TrackBy 函數用於優化 ngFor 性能
   */
  trackByFieldName(index: number, field: FormFieldConfig): string {
    return field.name || field.id || index.toString();
  }

  /**
   * 檢查是否有 reference data
   */
  hasReferenceData(): boolean {
    return this.formData && this.formData.referencedata && Object.keys(this.formData.referencedata).length > 0;
  }

  /**
   * 將 reference data 轉換為陣列格式以便顯示
   */
  getReferenceDataArray(): Array<{ label: string; value: string }> {
    if (!this.hasReferenceData()) {
      return [];
    }

    const referenceData = this.formData.referencedata;
    const result: Array<{ label: string; value: string }> = [];

    for (const key in referenceData) {
      if (referenceData.hasOwnProperty(key)) {
        const item = referenceData[key];
        result.push({
          label: item.label || key,
          value: item.value || '(無資料)'
        });
      }
    }

    return result;
  }
}
